import { useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import { useUnits } from "@/context/UnitContext";import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Plus, Trash2, MessageCircle, Instagram, Facebook, Save, UserPlus, Building2, Snowflake, Power, MessageSquare } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { toast } from "sonner";
import PageHeader from "@/components/PageHeader";

export default function Settings() {
  const { apiCall, user } = useAuth();
  const { units, selectedUnitId, reload: reloadUnits } = useUnits();
  const isMaster = user?.role === "master";
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [team, setTeam] = useState([]);
  const [memberForm, setMemberForm] = useState({ name: "", email: "", password: "", team_role: "recepcao", nickname: "", unit_ids: [] });
  const [memberSaving, setMemberSaving] = useState(false);
  const [unitForm, setUnitForm] = useState({ name: "", color: "#CBA871", address: "" });
  const [editingUnit, setEditingUnit] = useState(null);
  const [zapiDialog, setZapiDialog] = useState({ open: false, unit: null, instance_id: "", instance_token: "", client_token: "", saving: false });
  const [form, setForm] = useState({
    monthly_revenue_target: "",
    business_days_per_month: "",
    salon_name: "",
    salon_address: "",
    whatsapp_default_signature: "",
    whatsapp_accounts: [],
    instagram_handle: "",
    facebook_handle: "",
  });

  const load = async () => {
    setLoading(true);
    try {
      const [sRes, tRes] = await Promise.all([
        apiCall("get", "/settings"),
        apiCall("get", "/team/members"),
      ]);
      const d = sRes.data || {};
      setForm({
        monthly_revenue_target: (d.monthly_revenue_target ?? 0).toString(),
        business_days_per_month: (d.business_days_per_month ?? 26).toString(),
        salon_name: d.salon_name || "",
        salon_address: d.salon_address || "",
        whatsapp_default_signature: d.whatsapp_default_signature || "",
        whatsapp_accounts: d.whatsapp_accounts || [],
        instagram_handle: d.instagram_handle || "",
        facebook_handle: d.facebook_handle || "",
      });
      setTeam(tRes.data || []);
    } catch {
      toast.error("Erro ao carregar configurações");
    } finally {
      setLoading(false);
    }
  };

  const handleAddMember = async () => {
    if (!memberForm.name || !memberForm.email || !memberForm.password) {
      toast.error("Preencha nome, email e senha");
      return;
    }
    setMemberSaving(true);
    try {
      await apiCall("post", "/team/members", memberForm);
      toast.success(`${memberForm.name} adicionado ao time`);
      setMemberForm({ name: "", email: "", password: "", team_role: "recepcao", nickname: "", unit_ids: [] });
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao adicionar membro");
    } finally {
      setMemberSaving(false);
    }
  };

  const handleRemoveMember = async (id, name) => {
    if (!window.confirm(`Remover ${name} do time?`)) return;
    try {
      await apiCall("delete", `/team/members/${id}`);
      toast.success("Membro removido");
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  // === Unidades ===
  const handleSaveUnit = async () => {
    if (!unitForm.name.trim()) {
      toast.error("Nome obrigatório");
      return;
    }
    try {
      if (editingUnit) {
        await apiCall("put", `/units/${editingUnit}`, unitForm);
        toast.success("Unidade atualizada");
      } else {
        await apiCall("post", "/units", unitForm);
        toast.success("Unidade criada");
      }
      setUnitForm({ name: "", color: "#CBA871", address: "" });
      setEditingUnit(null);
      reloadUnits();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao salvar unidade");
    }
  };

  const handleEditUnit = (u) => {
    setEditingUnit(u.unit_id);
    setUnitForm({ name: u.name, color: u.color || "#CBA871", address: u.address || "" });
  };

  const handleDeleteUnit = async (u) => {
    if (!window.confirm(`Excluir a unidade "${u.name}"? Registros existentes vinculados serão desvinculados.`)) return;
    try {
      await apiCall("delete", `/units/${u.unit_id}`);
      toast.success("Unidade excluída");
      reloadUnits();
      load();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  const handleDeactivateUnit = async (u, opts = {}) => {
    const warn = (
      `Congelar a unidade "${u.name}"?\n\n` +
      "A unidade congelada NÃO participa de:\n" +
      "• envios de WhatsApp (Z-API)\n" +
      "• automações e scheduler\n" +
      "• campanhas e segmentos\n" +
      "• pre-launch-check (fica preservada em separado)\n\n" +
      "Nenhum dado ou histórico é apagado. Você pode reativar quando quiser."
    );
    if (!window.confirm(warn)) return;
    try {
      const headers = opts.forceLastActive ? { "X-Confirm-Last-Active": "1" } : {};
      await apiCall("patch", `/admin/units/${u.unit_id}/deactivate`, {}, { headers });
      toast.success(`Unidade "${u.name}" congelada`);
      reloadUnits();
    } catch (err) {
      const detail = err.response?.data?.detail || "";
      const status = err.response?.status;
      if (status === 409 && /última unidade ativa/i.test(detail)) {
        const extra = (
          `ATENÇÃO — ÚLTIMA UNIDADE ATIVA!\n\n` +
          `"${u.name}" é a única unidade ativa do salão. Congelar vai:\n` +
          "• interromper TODOS os envios reais de WhatsApp\n" +
          "• pausar TODAS as automações e scheduler\n" +
          "• bloquear qualquer disparo de segmento\n\n" +
          "Tem CERTEZA que quer continuar? (última confirmação)"
        );
        if (window.confirm(extra)) {
          await handleDeactivateUnit(u, { forceLastActive: true });
        }
        return;
      }
      toast.error(detail || "Erro ao congelar");
    }
  };

  const handleActivateUnit = async (u) => {
    if (!window.confirm(`Reativar a unidade "${u.name}"? Ela voltará a participar de envios, automações e disparos.`)) return;
    try {
      await apiCall("patch", `/admin/units/${u.unit_id}/activate`);
      toast.success(`Unidade "${u.name}" reativada`);
      reloadUnits();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro ao reativar");
    }
  };

  const openZapiDialog = (u) => {
    setZapiDialog({
      open: true,
      unit: u,
      instance_id: u.zapi_instance_id || "",
      instance_token: "",
      client_token: "",
      saving: false,
    });
  };
  const closeZapiDialog = () => setZapiDialog({ open: false, unit: null, instance_id: "", instance_token: "", client_token: "", saving: false });

  const handleSaveZapiInstance = async () => {
    const iid = (zapiDialog.instance_id || "").trim();
    if (!iid) {
      toast.error("Instance ID é obrigatório");
      return;
    }
    setZapiDialog((z) => ({ ...z, saving: true }));
    try {
      const body = { zapi_instance_id: iid };
      const itok = (zapiDialog.instance_token || "").trim();
      const ctok = (zapiDialog.client_token || "").trim();
      if (itok) body.zapi_instance_token = itok;
      if (ctok) body.zapi_client_token = ctok;
      const res = await apiCall("patch", `/admin/units/${zapiDialog.unit.unit_id}/zapi-instance`, body);
      toast.success(`Z-API configurada em "${zapiDialog.unit.name}" (${res.data.zapi_instance_id_masked})`);
      closeZapiDialog();
      reloadUnits();
    } catch (err) {
      const status = err.response?.status;
      const detail = err.response?.data?.detail || "Erro ao salvar";
      if (status === 409) toast.error(detail, { duration: 8000 });
      else toast.error(detail);
      setZapiDialog((z) => ({ ...z, saving: false }));
    }
  };

  const handleToggleMemberUnit = async (member, unit_id) => {
    const current = new Set(member.unit_ids || []);
    if (current.has(unit_id)) current.delete(unit_id);
    else current.add(unit_id);
    const arr = Array.from(current);
    try {
      await apiCall("patch", `/team/members/${member.user_id}/units`, { unit_ids: arr });
      // Atualiza local
      setTeam((prev) => prev.map((m) => m.user_id === member.user_id ? { ...m, unit_ids: arr } : m));
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erro");
    }
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, []);

  const addAccount = () => {
    setForm({ ...form, whatsapp_accounts: [...form.whatsapp_accounts, { label: "", phone: "", instagram_handle: "" }] });
  };
  const updateAccount = (i, field, value) => {
    const acc = [...form.whatsapp_accounts];
    acc[i] = { ...acc[i], [field]: value };
    setForm({ ...form, whatsapp_accounts: acc });
  };
  const removeAccount = (i) => {
    setForm({ ...form, whatsapp_accounts: form.whatsapp_accounts.filter((_, idx) => idx !== i) });
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await apiCall("put", "/settings", {
        monthly_revenue_target: parseFloat(form.monthly_revenue_target) || 0,
        business_days_per_month: parseInt(form.business_days_per_month) || 26,
        salon_name: form.salon_name,
        salon_address: form.salon_address,
        whatsapp_default_signature: form.whatsapp_default_signature,
        whatsapp_accounts: form.whatsapp_accounts.filter((a) => a.phone && a.label),
        instagram_handle: form.instagram_handle,
        facebook_handle: form.facebook_handle,
      });
      toast.success("Configurações salvas");
      load();
    } catch {
      toast.error("Erro ao salvar");
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="text-center py-12 text-muted-foreground">Carregando...</div>;

  return (
    <div className="space-y-6" data-testid="settings-page">
      <PageHeader overline="Configurações" title="Preferências do Salão" subtitle="Marca, metas e canais de comunicação" />

      {/* Trocar a própria senha — disponível pra todos os usuários */}
      <ChangePasswordCard apiCall={apiCall} />

      <form onSubmit={handleSave} className="space-y-6">
        <Card className="border border-border bg-card p-6 rounded-md shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Identidade</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Nome do salão</Label>
              <Input value={form.salon_name} onChange={(e) => setForm({ ...form, salon_name: e.target.value })} data-testid="settings-salon-name" placeholder="Ex: Gregory João" />
            </div>
            <div>
              <Label className="text-xs">Endereço</Label>
              <Input value={form.salon_address} onChange={(e) => setForm({ ...form, salon_address: e.target.value })} data-testid="settings-salon-address" />
            </div>
          </div>
        </Card>

        <Card className="border border-border bg-card p-6 rounded-md shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Metas Financeiras</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs">Meta de receita mensal (R$)</Label>
              <Input type="number" step="0.01" min="0" value={form.monthly_revenue_target} onChange={(e) => setForm({ ...form, monthly_revenue_target: e.target.value })} data-testid="settings-monthly-target" />
            </div>
            <div>
              <Label className="text-xs">Dias úteis por mês</Label>
              <Input type="number" min="1" max="31" value={form.business_days_per_month} onChange={(e) => setForm({ ...form, business_days_per_month: e.target.value })} data-testid="settings-business-days" />
            </div>
          </div>
        </Card>

        <Card className="border border-border bg-card p-6 rounded-md shadow-soft">
          <div className="flex items-center justify-between mb-4">
            <div className="text-xs uppercase tracking-widest text-muted-foreground flex items-center gap-2">
              <MessageCircle className="h-4 w-4" strokeWidth={1.5} style={{ color: "#25D366" }} />
              Contas de WhatsApp
            </div>
            <Button type="button" size="sm" variant="outline" onClick={addAccount} data-testid="settings-add-whatsapp">
              <Plus className="h-3 w-3 mr-1" strokeWidth={1.5} /> Adicionar
            </Button>
          </div>
          <p className="text-xs text-muted-foreground mb-3">Cadastre os números do salão (atendimento, comercial, profissional). Aparecem na hora de abrir conversa com a cliente.</p>
          {form.whatsapp_accounts.length === 0 ? (
            <div className="text-sm text-muted-foreground text-center py-6 border border-dashed border-border rounded">
              Nenhum número cadastrado ainda.
            </div>
          ) : (
            <div className="space-y-3">
              {form.whatsapp_accounts.map((acc, i) => (
                <div key={i} className="grid grid-cols-1 md:grid-cols-12 gap-2 items-end" data-testid={`whatsapp-account-${i}`}>
                  <div className="md:col-span-3">
                    <Label className="text-xs">Etiqueta</Label>
                    <Input value={acc.label} onChange={(e) => updateAccount(i, "label", e.target.value)} placeholder="Atendimento" />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-xs">Número (com DDD)</Label>
                    <Input value={acc.phone} onChange={(e) => updateAccount(i, "phone", e.target.value)} placeholder="+55 11 99999-9999" />
                  </div>
                  <div className="md:col-span-4">
                    <Label className="text-xs">@instagram vinculado (opcional)</Label>
                    <Input value={acc.instagram_handle || ""} onChange={(e) => updateAccount(i, "instagram_handle", e.target.value)} placeholder="@gregoryjoao" />
                  </div>
                  <div className="md:col-span-1">
                    <Button type="button" size="icon" variant="ghost" onClick={() => removeAccount(i)} data-testid={`whatsapp-remove-${i}`}>
                      <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="mt-4">
            <Label className="text-xs">Assinatura padrão das mensagens (opcional)</Label>
            <Input value={form.whatsapp_default_signature} onChange={(e) => setForm({ ...form, whatsapp_default_signature: e.target.value })} placeholder="Equipe Gregory João · Confia, eu faço!" />
          </div>
        </Card>

        <Card className="border border-border bg-card p-6 rounded-md shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4">Redes Sociais</div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs flex items-center gap-2"><Instagram className="h-3 w-3" strokeWidth={1.5} /> Instagram do salão</Label>
              <Input value={form.instagram_handle} onChange={(e) => setForm({ ...form, instagram_handle: e.target.value })} placeholder="@gregoryjoao" data-testid="settings-instagram" />
            </div>
            <div>
              <Label className="text-xs flex items-center gap-2"><Facebook className="h-3 w-3" strokeWidth={1.5} /> Facebook do salão</Label>
              <Input value={form.facebook_handle} onChange={(e) => setForm({ ...form, facebook_handle: e.target.value })} placeholder="gregoryjoao" data-testid="settings-facebook" />
            </div>
          </div>
        </Card>

        <Card className="border border-border bg-card p-6 rounded-md shadow-soft" data-testid="units-card">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <Building2 className="h-4 w-4" strokeWidth={1.5} /> Unidades (multi-loja)
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Cadastre cada unidade do salão (ex: <strong>Cisper</strong>, <strong>Unidade B</strong>, <strong>Estética</strong>, <strong>Salão</strong>).
            Funcionários vinculados a uma unidade veem somente os dados dela. O Master vê tudo (com filtro opcional no topo).
          </p>

          {units.length > 0 && (
            <div className="space-y-2 mb-4">
              {units.map((u) => {
                const isActive = u.active !== false;
                const hasIid = !!(u.zapi_instance_id && u.zapi_instance_id.length > 0);
                const iidMasked = hasIid ? (u.zapi_instance_id.length > 8 ? `${u.zapi_instance_id.slice(0, 6)}…${u.zapi_instance_id.slice(-4)}` : u.zapi_instance_id) : null;
                const tokensSet = !!u.zapi_instance_token_set && !!u.zapi_client_token_set;
                return (
                <div key={u.unit_id} className={`p-3 border rounded-md ${isActive ? "border-border" : "border-border/60 bg-muted/30"}`} data-testid={`unit-row-${u.unit_id}`}>
                  <div className="flex items-center gap-3">
                    <div className="w-3 h-8 rounded" style={{ background: u.color || "#CBA871", opacity: isActive ? 1 : 0.35 }} />
                    <div className="flex-1">
                      <div className="text-sm font-medium flex items-center gap-2 flex-wrap">
                        <span className={isActive ? "" : "text-muted-foreground line-through"}>{u.name}</span>
                        {isActive ? (
                          <span
                            className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-emerald-100 text-emerald-700 border border-emerald-200"
                            data-testid={`unit-badge-active-${u.unit_id}`}
                          >
                            Ativa
                          </span>
                        ) : (
                          <span
                            className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded bg-slate-200 text-slate-600 border border-slate-300 flex items-center gap-1"
                            data-testid={`unit-badge-frozen-${u.unit_id}`}
                          >
                            <Snowflake className="h-3 w-3" strokeWidth={1.8} /> Congelada
                          </span>
                        )}
                      </div>
                      {u.address && <div className="text-xs text-muted-foreground">{u.address}</div>}
                      {!isActive && (
                        <div className="text-[11px] text-muted-foreground mt-1">
                          Não participa de envios, automações, scheduler nem segmentos. Dados preservados.
                        </div>
                      )}
                    </div>
                    {isMaster && (
                      <>
                        {isActive ? (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleDeactivateUnit(u)}
                            data-testid={`unit-freeze-btn-${u.unit_id}`}
                            className="text-slate-600 hover:text-slate-800"
                            title="Congelar unidade (não apaga dados)"
                          >
                            <Snowflake className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Congelar
                          </Button>
                        ) : (
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => handleActivateUnit(u)}
                            data-testid={`unit-reactivate-btn-${u.unit_id}`}
                            className="text-emerald-700 hover:text-emerald-800"
                            title="Reativar unidade"
                          >
                            <Power className="h-3.5 w-3.5 mr-1" strokeWidth={1.8} /> Reativar
                          </Button>
                        )}
                        <Button type="button" variant="ghost" size="sm" onClick={() => handleEditUnit(u)} data-testid={`unit-edit-${u.unit_id}`}>
                          Editar
                        </Button>
                        <Button type="button" variant="ghost" size="icon" onClick={() => handleDeleteUnit(u)} data-testid={`unit-delete-${u.unit_id}`}>
                          <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                        </Button>
                      </>
                    )}
                  </div>
                  {isMaster && isActive && (
                    <div className="mt-2 pt-2 border-t border-dashed border-border/60 flex items-center gap-2 flex-wrap">
                      <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" strokeWidth={1.5} />
                      <span className="text-[11px] uppercase tracking-wider text-muted-foreground">Z-API:</span>
                      {hasIid ? (
                        <>
                          <span
                            className="text-[11px] font-mono px-2 py-0.5 rounded bg-emerald-50 text-emerald-700 border border-emerald-200"
                            data-testid={`unit-zapi-id-${u.unit_id}`}
                            title={`Instance ID: ${iidMasked}`}
                          >
                            {iidMasked}
                          </span>
                          <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${tokensSet ? "bg-emerald-50 text-emerald-700 border-emerald-200" : "bg-amber-50 text-amber-700 border-amber-200"}`}>
                            {tokensSet ? "tokens ok" : "tokens pendentes"}
                          </span>
                        </>
                      ) : (
                        <span className="text-[11px] px-2 py-0.5 rounded bg-amber-50 text-amber-700 border border-amber-200" data-testid={`unit-zapi-missing-${u.unit_id}`}>
                          Não configurada
                        </span>
                      )}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="ml-auto text-[11px] h-7"
                        onClick={() => openZapiDialog(u)}
                        data-testid={`unit-zapi-config-btn-${u.unit_id}`}
                      >
                        {hasIid ? "Alterar Z-API" : "Configurar Z-API"}
                      </Button>
                    </div>
                  )}
                </div>
                );
              })}
            </div>
          )}

          {isMaster && (
            <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end pt-2 border-t border-border">
              <div className="md:col-span-2">
                <Label className="text-xs">Nome da unidade</Label>
                <Input value={unitForm.name} onChange={(e) => setUnitForm({ ...unitForm, name: e.target.value })} placeholder="Cisper" data-testid="unit-name-input" />
              </div>
              <div className="md:col-span-2">
                <Label className="text-xs">Endereço (opcional)</Label>
                <Input value={unitForm.address} onChange={(e) => setUnitForm({ ...unitForm, address: e.target.value })} placeholder="Rua X, 123" data-testid="unit-address-input" />
              </div>
              <div className="md:col-span-1">
                <Label className="text-xs">Cor</Label>
                <div className="flex gap-2 items-center">
                  <input
                    type="color"
                    value={unitForm.color}
                    onChange={(e) => setUnitForm({ ...unitForm, color: e.target.value })}
                    className="h-9 w-12 rounded border border-input cursor-pointer"
                    data-testid="unit-color-input"
                  />
                  <Button type="button" size="sm" onClick={handleSaveUnit} className="flex-1" data-testid="unit-save-btn">
                    {editingUnit ? "Atualizar" : <Plus className="h-3 w-3" strokeWidth={1.5} />}
                  </Button>
                  {editingUnit && (
                    <Button type="button" size="sm" variant="ghost" onClick={() => { setEditingUnit(null); setUnitForm({ name: "", color: "#CBA871", address: "" }); }} data-testid="unit-cancel-btn">
                      Cancelar
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </Card>

        <Card className="border border-border bg-card p-6 rounded-md shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-4 flex items-center gap-2">
            <UserPlus className="h-4 w-4" strokeWidth={1.5} /> Equipe com login
          </div>
          <p className="text-xs text-muted-foreground mb-3">
            Cadastre logins compartilhados do salão (Manu - Recepção, Gerente, etc). Eles podem ser mencionados com <code className="bg-blue-100 text-blue-800 px-1 rounded">=apelido</code> nas tarefas.
          </p>
          {team.length > 0 && (
            <div className="space-y-2 mb-4">
              {team.map((m) => {
                const isMe = m.user_id === user?.user_id;
                const isRowMaster = !m.team_master_id;
                return (
                  <div key={m.user_id} className="flex flex-col gap-2 p-3 border border-border rounded-md" data-testid={`team-member-${m.user_id}`}>
                    <div className="flex items-center gap-3">
                      <div className="flex-1">
                        <div className="text-sm font-medium">
                          {m.nickname && <span className="text-blue-700">={m.nickname}</span>}{" "}
                          <span>{m.name}</span>
                          {isMe && <span className="ml-2 text-[10px] uppercase tracking-widest text-muted-foreground">(você)</span>}
                          {isRowMaster && <span className="ml-2 text-[10px] uppercase tracking-widest text-amber-700">master</span>}
                        </div>
                        <div className="text-xs text-muted-foreground">{m.email} · {m.team_role || "—"}</div>
                      </div>
                      {!isMe && !isRowMaster && (
                        <>
                          <Button type="button" variant="ghost" size="sm" onClick={async () => {
                            const np = window.prompt(`Nova senha para ${m.name}:`);
                            if (!np || np.length < 6) { toast.error("Senha precisa ter no mínimo 6 caracteres"); return; }
                            try {
                              await apiCall("post", `/team/members/${m.user_id}/reset-password`, { new_password: np });
                              toast.success(`Senha de ${m.name} atualizada`);
                            } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
                          }} className="text-xs" data-testid={`reset-pw-${m.user_id}`}>
                            🔑 Resetar senha
                          </Button>
                          <Button type="button" variant="ghost" size="icon" onClick={() => handleRemoveMember(m.user_id, m.name)} data-testid={`team-remove-${m.user_id}`}>
                            <Trash2 className="h-4 w-4 text-destructive" strokeWidth={1.5} />
                          </Button>
                        </>
                      )}
                    </div>
                    {isMaster && units.length > 0 && !isRowMaster && (
                      <div className="flex flex-wrap items-center gap-2 pt-2 border-t border-border">
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Unidades:</span>
                        {units.map((u) => {
                          const checked = (m.unit_ids || []).includes(u.unit_id);
                          return (
                            <label
                              key={u.unit_id}
                              className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-full border cursor-pointer transition ${checked ? "border-foreground bg-secondary" : "border-border opacity-70 hover:opacity-100"}`}
                              data-testid={`member-unit-${m.user_id}-${u.unit_id}`}
                            >
                              <input
                                type="checkbox"
                                checked={checked}
                                onChange={() => handleToggleMemberUnit(m, u.unit_id)}
                                className="h-3 w-3"
                              />
                              <span className="w-2 h-2 rounded-full" style={{ background: u.color || "#CBA871" }} />
                              {u.name}
                            </label>
                          );
                        })}
                        {(m.unit_ids || []).length === 0 && (
                          <span className="text-[10px] text-muted-foreground italic">Nenhuma → vê todas as unidades</span>
                        )}
                      </div>
                    )}
                    {isMaster && !isRowMaster && (
                      <div className="flex items-center gap-2 pt-2 border-t border-border">
                        <span className="text-[10px] uppercase tracking-widest text-muted-foreground">Permissões:</span>
                        <label className="flex items-center gap-1.5 text-xs cursor-pointer" data-testid={`dispatch-perm-${m.user_id}`}>
                          <input
                            type="checkbox"
                            className="h-3 w-3"
                            checked={!!m.can_manual_dispatch}
                            onChange={async (e) => {
                              const next = e.target.checked;
                              try {
                                await apiCall("patch", `/team/members/${m.user_id}/dispatch-permission`, { can_manual_dispatch: next });
                                toast.success(`${next ? "Liberado" : "Bloqueado"} disparo manual para ${m.name}`);
                                await load();
                              } catch (err) { toast.error(err.response?.data?.detail || "Erro"); }
                            }}
                          />
                          <span>Pode disparar Segmentos</span>
                        </label>
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
          <div className="grid grid-cols-1 md:grid-cols-5 gap-2 items-end pt-2 border-t border-border">
            <div className="md:col-span-1">
              <Label className="text-xs">Apelido</Label>
              <Input value={memberForm.nickname} onChange={(e) => setMemberForm({ ...memberForm, nickname: e.target.value })} placeholder="manu" data-testid="member-nickname" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs">Nome</Label>
              <Input value={memberForm.name} onChange={(e) => setMemberForm({ ...memberForm, name: e.target.value })} placeholder="Manu Silva" data-testid="member-name" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs">Email</Label>
              <Input type="email" value={memberForm.email} onChange={(e) => setMemberForm({ ...memberForm, email: e.target.value })} placeholder="manu@salao.com" data-testid="member-email" />
            </div>
            <div className="md:col-span-1">
              <Label className="text-xs">Senha inicial</Label>
              <Input type="text" value={memberForm.password} onChange={(e) => setMemberForm({ ...memberForm, password: e.target.value })} placeholder="senha@123" data-testid="member-password" />
            </div>
            <div className="md:col-span-1 flex gap-2">
              <select
                value={memberForm.team_role}
                onChange={(e) => setMemberForm({ ...memberForm, team_role: e.target.value })}
                className="h-9 rounded-md border border-input bg-transparent px-2 text-sm flex-1"
                data-testid="member-role"
              >
                <option value="recepcao">Recepção</option>
                <option value="gerencia">Gerência</option>
                <option value="profissional">Profissional</option>
                <option value="limitado">Funcionário (limitado)</option>
              </select>
              <Button type="button" size="sm" disabled={memberSaving} onClick={handleAddMember} data-testid="member-add">
                <Plus className="h-3 w-3" strokeWidth={1.5} />
              </Button>
            </div>
          </div>
        </Card>

        <Card className="border border-border bg-card p-6 rounded-md shadow-soft">
          <div className="text-xs uppercase tracking-widest text-muted-foreground mb-2 flex items-center gap-2">
            📥 Importar Dados Legados
          </div>
          {(() => {
            const targetUnit = units.find((u) => u.unit_id === selectedUnitId);
            const noUnitChosen = !selectedUnitId || selectedUnitId === "all";
            return (
              <>
                <p className="text-xs text-muted-foreground mb-2">
                  Envie um arquivo .zip OU um .csv solto. O sistema detecta o tipo pelo cabeçalho:
                  <code className="mx-1">colaborador.csv</code>,
                  <code className="mx-1">servico.csv</code>,
                  <code className="mx-1">cliente_dados.csv</code>,
                  <code className="mx-1">atendimento.csv</code>,
                  <code className="mx-1">estoque.csv</code>,
                  <code className="mx-1">pacote.csv</code>,
                  <code className="mx-1">cliente_saldo.csv</code>.
                </p>
                <div className={`p-2 mb-3 rounded-md text-xs ${noUnitChosen ? 'bg-red-50 text-red-900 border border-red-200' : 'bg-emerald-50 text-emerald-900 border border-emerald-200'}`} data-testid="import-target-banner">
                  {noUnitChosen ? (
                    <>⚠ <strong>Nenhuma unidade selecionada.</strong> Selecione uma unidade no topo da página antes de importar — os dados precisam ir para uma unidade específica.</>
                  ) : (
                    <>✓ Os dados serão importados para a unidade: <strong>{targetUnit?.name || selectedUnitId}</strong></>
                  )}
                </div>
                <input
                  type="file"
                  accept=".zip,.csv"
                  disabled={noUnitChosen}
                  onChange={async (e) => {
                    const f = e.target.files?.[0];
                    if (!f) return;
                    if (noUnitChosen) {
                      toast.error("Selecione uma unidade antes de importar");
                      e.target.value = "";
                      return;
                    }
                    const fd = new FormData();
                    fd.append("file", f);
                    const t = toast.loading(`Importando ${f.name} (${(f.size/1024).toFixed(0)}KB) → ${targetUnit?.name}...`);
                    // Usa XMLHttpRequest em vez de fetch para evitar o problema de
                    // "body stream already read" causado por service workers/extensions
                    // do navegador que interceptam o fetch.
                    const xhrResult = await new Promise((resolve) => {
                      const url = `${process.env.REACT_APP_BACKEND_URL}/api/import/legacy-zip`;
                      const tk = localStorage.getItem("access_token");
                      const xhr = new XMLHttpRequest();
                      xhr.open("POST", url, true);
                      xhr.setRequestHeader("Authorization", `Bearer ${tk}`);
                      xhr.setRequestHeader("X-Unit-Id", selectedUnitId);
                      xhr.timeout = 180000; // 3 minutos
                      xhr.onload = () => resolve({ status: xhr.status, text: xhr.responseText || "" });
                      xhr.onerror = () => resolve({ status: 0, text: "", error: "network_error" });
                      xhr.ontimeout = () => resolve({ status: 0, text: "", error: "timeout" });
                      xhr.send(fd);
                    });
                    toast.dismiss(t);
                    let data = {};
                    try {
                      data = xhrResult.text ? JSON.parse(xhrResult.text) : {};
                    } catch {
                      data = {
                        ok: false,
                        detail: `Resposta não-JSON (${xhrResult.text.length}B):\n${xhrResult.text.slice(0, 600)}`,
                      };
                    }
                    if (xhrResult.error) {
                      toast.error(`Erro de rede: ${xhrResult.error}`);
                      alert(`❌ Erro de rede: ${xhrResult.error}\n\nO servidor não respondeu (timeout 3min ou conexão recusada).\nVerifique sua internet e tente novamente.`);
                    } else if (xhrResult.status === 200 && data.ok) {
                      toast.success(`Import concluído em ${targetUnit?.name} ✓`);
                      const detected = (data.detected || []).map(d =>
                        d.detected_as
                          ? `✓ ${d.file} → ${d.detected_as}`
                          : `✗ ${d.file} → não reconhecido (cabeçalho: ${(d.header_preview||[]).join(", ")})`
                      ).join("\n");
                      alert(`📋 ARQUIVOS DETECTADOS:\n${detected || "(nenhum)"}\n\n📊 RESULTADO:\n${data.stdout || "(vazio)"}`);
                    } else {
                      toast.error(`Erro HTTP ${xhrResult.status}: ${data.detail || "Falhou"}`);
                      const detected = (data.detected || []).map(d =>
                        d.detected_as
                          ? `✓ ${d.file} → ${d.detected_as}`
                          : `✗ ${d.file} → não reconhecido`
                      ).join("\n");
                      alert(`❌ HTTP ${xhrResult.status}\n\n${data.detail || xhrResult.text.slice(0, 500)}\n\n📋 ARQUIVOS DETECTADOS:\n${detected || "(nenhum)"}\n\n${data.stderr ? "STDERR:\n" + data.stderr : ""}`);
                    }
                    e.target.value = "";
                  }}
                  data-testid="import-file-input"
                  className={`block text-sm ${noUnitChosen ? 'opacity-40 cursor-not-allowed' : ''}`}
                />
              </>
            );
          })()}
          <p className="text-[10px] text-muted-foreground mt-2">
            ⚠ O import é idempotente — pode rodar várias vezes sem duplicar registros. Cada importação grava na unidade ativa.
          </p>
        </Card>

        <div className="flex justify-end">
          <Button type="submit" disabled={saving} data-testid="settings-save">
            <Save className="h-4 w-4 mr-2" strokeWidth={1.5} />
            {saving ? "Salvando..." : "Salvar Configurações"}
          </Button>
        </div>
      </form>

      <Dialog open={zapiDialog.open} onOpenChange={(o) => (o ? null : closeZapiDialog())}>
        <DialogContent className="max-w-lg" data-testid="zapi-config-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <MessageSquare className="h-4 w-4" strokeWidth={1.5} />
              Configurar Z-API — {zapiDialog.unit?.name}
            </DialogTitle>
            <DialogDescription className="text-xs leading-relaxed">
              Cadastre o WhatsApp desta unidade. Só o <strong>Instance ID</strong> é obrigatório; os tokens só precisam
              ser cadastrados se você quiser fazer envios reais. Nada é vazado — tokens ficam mascarados na resposta.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="rounded border border-amber-200 bg-amber-50 text-amber-900 p-2 text-[11px] leading-relaxed">
              <strong>Onde encontrar:</strong> painel <a href="https://app.z-api.io" target="_blank" rel="noreferrer" className="underline">app.z-api.io</a> →
              Suas Instâncias → engrenagem da instância desta unidade → copie <em>Instance ID</em>, <em>Instance Token</em> e <em>Client Token</em>. Cada Instance ID só pode servir 1 unidade (a UI valida).
            </div>
            <div>
              <Label className="text-xs">Instance ID <span className="text-destructive">*</span></Label>
              <Input
                value={zapiDialog.instance_id}
                onChange={(e) => setZapiDialog((z) => ({ ...z, instance_id: e.target.value }))}
                placeholder="Ex: 3E3A9F... (~32 caracteres)"
                data-testid="zapi-instance-id-input"
                autoFocus
              />
            </div>
            <div>
              <Label className="text-xs">Instance Token (opcional agora)</Label>
              <Input
                type="password"
                value={zapiDialog.instance_token}
                onChange={(e) => setZapiDialog((z) => ({ ...z, instance_token: e.target.value }))}
                placeholder={zapiDialog.unit?.zapi_instance_token_set ? "Já configurado — deixe vazio para manter" : "cole o Instance Token"}
                data-testid="zapi-instance-token-input"
              />
              {zapiDialog.unit?.zapi_instance_token_set && (
                <div className="text-[10px] text-emerald-700 mt-0.5">✓ Já configurado. Só preencha se quiser trocar.</div>
              )}
            </div>
            <div>
              <Label className="text-xs">Client Token (Account Security Token)</Label>
              <Input
                type="password"
                value={zapiDialog.client_token}
                onChange={(e) => setZapiDialog((z) => ({ ...z, client_token: e.target.value }))}
                placeholder={zapiDialog.unit?.zapi_client_token_set ? "Já configurado — deixe vazio para manter" : "cole o Client Token"}
                data-testid="zapi-client-token-input"
              />
              {zapiDialog.unit?.zapi_client_token_set && (
                <div className="text-[10px] text-emerald-700 mt-0.5">✓ Já configurado. Só preencha se quiser trocar.</div>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={closeZapiDialog} disabled={zapiDialog.saving} data-testid="zapi-cancel-btn">Cancelar</Button>
            <Button onClick={handleSaveZapiInstance} disabled={zapiDialog.saving || !zapiDialog.instance_id.trim()} data-testid="zapi-save-btn">
              {zapiDialog.saving ? "Salvando..." : "Salvar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* Card pra trocar a própria senha. Disponível pra qualquer papel. */
function ChangePasswordCard({ apiCall }) {
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState("");
  const [next, setNext] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    if (next.length < 6) { toast.error("A nova senha precisa ter pelo menos 6 caracteres"); return; }
    if (next !== confirm) { toast.error("As duas novas senhas não batem"); return; }
    setSaving(true);
    try {
      await apiCall("post", "/auth/change-password", { current_password: current, new_password: next });
      toast.success("Senha atualizada! Outros dispositivos foram deslogados.");
      setCurrent(""); setNext(""); setConfirm(""); setOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Erro ao trocar senha");
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card className="border border-border bg-card p-6 rounded-md shadow-soft" data-testid="change-password-card">
      <div className="flex items-center justify-between mb-2">
        <div className="text-xs uppercase tracking-widest text-muted-foreground">🔐 Minha senha</div>
        {!open && (
          <Button type="button" variant="outline" size="sm" onClick={() => setOpen(true)} data-testid="change-password-btn">
            Trocar senha
          </Button>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="grid grid-cols-1 md:grid-cols-3 gap-3 mt-2">
          <div>
            <Label className="text-xs">Senha atual</Label>
            <Input type="password" value={current} onChange={(e) => setCurrent(e.target.value)} data-testid="current-password-input" autoComplete="current-password" required />
          </div>
          <div>
            <Label className="text-xs">Nova senha</Label>
            <Input type="password" value={next} onChange={(e) => setNext(e.target.value)} data-testid="new-password-input" autoComplete="new-password" minLength={6} required />
          </div>
          <div>
            <Label className="text-xs">Confirmar nova senha</Label>
            <Input type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} data-testid="confirm-password-input" autoComplete="new-password" minLength={6} required />
          </div>
          <div className="md:col-span-3 flex items-center gap-2">
            <Button type="submit" disabled={saving} data-testid="save-password-btn">
              {saving ? "Salvando…" : "Salvar nova senha"}
            </Button>
            <Button type="button" variant="ghost" onClick={() => { setOpen(false); setCurrent(""); setNext(""); setConfirm(""); }} disabled={saving}>
              Cancelar
            </Button>
            <span className="text-[11px] text-muted-foreground ml-auto">Mínimo 6 caracteres. Outros dispositivos serão deslogados.</span>
          </div>
        </form>
      )}
    </Card>
  );
}

